import { gql } from "@apollo/client";
import {
  TProgram,
  TProgramDateOption,
  TProgramMonth,
  TProgramPricing,
} from "types/program";

// Get program with limited data
export const GET_PROGRAM_LIMITED = gql`
  query GetProgram($id: ID!) {
    # Define the variable $id and its type
    program(id: $id) {
      # Use the variable $id in the query
      name
      arabicName
      allowedNationality
      programType
      departmentName
      pk
      id
      fees
      pricing {
        edges {
          node {
            price
            pk
            title
            isCustom
          }
        }
      }
      paymentOption
      platform
      preProgramQuestions
      deactivateApplicationFees
      isPublished
      months {
        pk
        id
        month
        startingDate
        endingDate
        registrationClosed
      }
      date_options: dateOptions {
        id: pk
        date
        label
        registration_limit: registrationLimit
        registration_closed: registrationClosed
        allow_registration: allowRegistration
        remaining_seats: remainingSeats
        registered_count: registeredCount
      }
      termsAndConditions
      ageFrom
      ageTo
      startingDate
    }
  }
`;

//program on payment form
export const GET_PROGRAM_PAYMENT_FORM = gql`
  query GetProgramPaymentForm($id: ID!) {
    # Define the variable $id and its type
    program(id: $id) {
      # Use the variable $id in the query
      name
      departmentName
      programType
      pk
      id
      fees
      discount
      discountDeadline
      hasActiveCoupons
      deactivateApplicationFees
      applicationFees
      paymentOption
    }
  }
`;

export const GET_PUBLIC_PROGRAMS = gql`
  query GetPublicPrograms(
    $first: Int
    $after: String
    $orderBy: String
    $ages: String
    $affiliates: String
    $languages: String
    $search: String
    $months: String
    $year: String
    $statuses: String
    $volunteer: Boolean
    $coreAreas: String
    $supervisorCode: String
    $offset: Int
  ) {
    programs(
      first: $first
      after: $after
      orderBy: $orderBy
      ages: $ages
      affiliates: $affiliates
      languages: $languages
      search: $search
      months: $months
      year: $year
      statuses: $statuses
      volunteer: $volunteer
      coreAreas: $coreAreas
      supervisorCode: $supervisorCode
      offset: $offset
    ) {
      totalCount
      edgeCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }

      edges {
        node {
          name
          arabicName
          noStyleDescription
          noStyleArabicDescription
          arabicImage
          image
          departmentName
          startingDate
          endingDate
          limit
          programType
          registrationClosed
          pk
          id
          ageFrom
          ageTo
          initiative {
            pk
          }
          isClosed
          isRegistered
          isUpcoming
          isPublished
          firstItem
          sections {
            titleEn
            descriptionEn
            descriptionAr
          }
          imageTag {
            title
            bgColor
            isClosed
          }
        }
      }
    }
  }
`;

export const GET_TRAINERS = gql`
  query GetActiveTrainers($isActive: Boolean) {
    trainers(isActive: $isActive) {
      edgeCount
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
      edges {
        node {
          pk
          id
          name
          isActive
        }
      }
    }
  }
`;

export const MUTATION_TRAINER = gql`
  mutation CreateUpdateTrainer($name: String!, $isActive: Boolean!, $id: ID) {
    createUpdateTrainer(input: { name: $name, isActive: $isActive, id: $id }) {
      obj {
        pk
        id
        name
        isActive
      }
      errors {
        messages
      }
    }
  }
`;

export const DELETE_TRAINER = gql`
  mutation DeleteTrainer($id: ID!) {
    deleteTrainer(id: $id) {
      deleted
    }
  }
`;

export const GET_UPCOMING_INTERNSHIP_PROGRAMS = gql`
  query GetUpcomingInternshipPrograms($upcomingInternshipPrograms: Boolean!) {
    programs(upcomingInternshipPrograms: $upcomingInternshipPrograms) {
      edges {
        node {
          pk
          id
          name
          phase
          startingDate
          termsAndConditions
          ageFrom
          ageTo
          defaultInternshipCompensation
        }
      }
    }
  }
`;

export const GET_PROGRAM_ATTENDANCE = gql`
  query GetProgram($id: ID!) {
    # Define the variable $id and its type
    program(id: $id) {
      name
      startingDate
      endingDate
      startingTime
      endingTime
      programType
      months {
        id
      }
      dateOptions {
        id
        date
      }
      differentAttendanceSchedule
      attendance {
        edges {
          node {
            status
            date
            timeIn
            timeOut
            comment
            applicant {
              id
              pk
            }
          }
        }
      }
      registrations {
        edges {
          node {
            status
            assignedCompany
            assignedBranch
            assignedDepartment
            monthChoice {
              month
              startingDate
            }
            dateChoice {
              date
              label
            }
            applicant {
              id
              pk
              firstName
              lastName
              user {
                id
                pk
              }
            }
          }
        }
      }
    }
  }
`;

// types
export type TGetProgramLimited = {
  pk: number;
  pricing: {
    edges: {
      nodes: TProgramPricing[];
    };
  };
  months: TProgramMonth[];
  date_options?: TProgramDateOption[];
} & TProgram;
export const GET_CORE_PROGRAMS = gql`
  query {
    corePrograms {
      edges {
        node {
          title
        }
      }
    }
  }
`;

export const GET_STAFF_DASHBOARD_REGISTRATIONS_DATA = gql`
  query ProgramNode(
    $year: String!
    $selectedMonths: String!
    $status: String!
    $coreArea: String!
    $programName: String!
    $affiliate: String!
    $registrationFees: String!
    $programFees: String!
    $smjMonth: String!
    $staffDashboard: Boolean!
  ) {
    programs(
      year: $year
      selectedMonths: $selectedMonths
      status: $status
      coreArea: $coreArea
      programName: $programName
      affiliate: $affiliate
      programFees: $programFees
      registrationFees: $registrationFees
      smjMonth: $smjMonth
      staffDashboard: $staffDashboard
    ) {
      totalCount
      edges {
        node {
          pk
          name
          applicantsCount
          startingDate
          programFeesPaidCount
          registrationFeesPaidCount
          fees
          departmentName
          target
          targetDate
          targetCount
          months {
            pk
            month
          }
          dateOptions {
            pk
            date
            label
          }
          coreProgram {
            area
          }
        }
      }
    }
  }
`;

export const GET_STAFF_DASHBOARD_PROGRAMS_LIST = gql`
  query ProgramsList {
    programs(staffDashboard: true) {
      edges {
        node {
          name
          pk
          programType
          months {
            pk
            month
          }
          dateOptions {
            pk
            date
            label
          }
        }
      }
    }
  }
`;

export const GET_MONTHS_CHILDREN_REG_INFO = gql`
  query GetProgramDetailsPageData($id: ID!, $monthId: Int) {
    program(id: $id) {
      months(pk: $monthId) {
        pk
        childrenRegistrationInfo {
          applicant {
            avatarData
            leaderboardDisplay
            profileImage
            civilId
            fullName
            arabicFullName
            age
            pk
            user {
              pk
            }
          }
          darbiStatus
          darbi2Slot
          darbiSlot
          firstInterviewSlot
          jobsiteSlot
          canUpdateDarbi
          status
          registerBtnText
          registrationId
          canProceed
          newRegistration
          paid
        }
      }
    }
  }
`;

export const GET_PROGRAM_CHILDREN_REG_INFO = gql`
  query GetProgramChildrenRegInfo($id: ID!) {
    program(id: $id) {
      childrenRegistrationInfo {
        applicant {
          avatarData
          leaderboardDisplay
          profileImage
          civilId
          fullName
          arabicFullName
          age
          pk
          user {
            pk
          }
        }
        darbiStatus
        darbi2Slot
        darbiSlot
        firstInterviewSlot
        jobsiteSlot
        canUpdateDarbi
        status
        registerBtnText
        registrationId
        canProceed
        newRegistration
        paid
      }
    }
  }
`;
